import { useAuthorization } from "@common/providers";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { Route, Routes } from "react-router";
import Show from "./Show";
import {
  useGetAccountQuery,
  useGetProjectByIdQuery,
  useWhoAmIQuery,
} from "@generated";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import Edit from "./Edit/Edit";
import New from "./New/New";
import useFlags from "@common/hooks/useFlags";

const VendorQuotesV2Mspa = ({ project_id }): JSX.Element => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  // Get Account
  const { data: account, isLoading: accountLoading } = useGetAccountQuery({
    slug: accountSlug,
    include: ["phases", "resources"],
  });

  // Get Project with included items
  const { data: project, isLoading: projectLoading } = useGetProjectByIdQuery({
    id: project_id,
    slug: accountSlug,
    include: [
      "project-phases",
      "payment-term",
      "project-resources",
      "project-services",
      "rate-table",
      "quotes",
    ],
  });

  // Get permissions for current user
  const { data: currentUser, isLoading: currentUserLoading } = useWhoAmIQuery();

  const authorized = useAuthorization();

  const privileges = currentUser?.data?.attributes?.privileges || [];
  const permissions = {
    quotes: privileges.find((p) => p.privilege === "projects.vendor_quotes")?.[
      "access-level"
    ],
    vendors: privileges.find((p) => p.privilege === "settings.vendors")?.[
      "access-level"
    ],
  };

  const { configVendorQuoteRevenueCalculation } = useFlags();

  if (!authorized || accountLoading || currentUserLoading || projectLoading) {
    return <ScopeStackSpinner />;
  }

  const labels = {
    quote:
      account?.data?.attributes?.["field-labels"]?.["vendor_quote"] ||
      "Vendor Quote",
    resource:
      account?.data?.attributes?.["field-labels"]?.["vendor_resource"] ||
      "Vendor Resource",
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <Show
            project_id={project_id}
            account={account}
            permissions={permissions}
            project={project}
            revenueCalculationLabel={configVendorQuoteRevenueCalculation}
            labels={labels}
          />
        }
      />
      <Route
        path="/edit/:quoteId"
        element={
          <Edit
            project_id={project_id}
            account={account}
            permissions={permissions}
            project={project}
            revenueCalculationLabel={configVendorQuoteRevenueCalculation}
            labels={labels}
          />
        }
      />
      <Route
        path="/new"
        element={
          <New
            project_id={project_id}
            account={account}
            permissions={permissions}
            project={project}
            revenueCalculationLabel={configVendorQuoteRevenueCalculation}
            labels={labels}
          />
        }
      />
    </Routes>
  );
};

export default VendorQuotesV2Mspa;
